import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  makeStyles,
  Typography
} from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  screenshotLink: {
    display: 'block',
    marginTop: theme.spacing(1)
  },
  screenshot: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 480,
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'zoom-in'
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  snapshotPre: {
    margin: 0,
    padding: theme.spacing(1.5),
    width: '100%',
    maxHeight: 420,
    overflow: 'auto',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
    borderRadius: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
}));

/**
 * Playwright failure artifacts (screenshot inline, HTML/trace as links).
 * Shared by the LQA and MPG QA submission drawers.
 */
export const DebugArtifactsSection = ({ artifacts }) => {
  const classes = useStyles();
  if (!artifacts) return null;
  const { screenshotS3Url, htmlSnapshotS3Url, playwrightTraceS3Url, aspireUrl } = artifacts;
  if (!screenshotS3Url && !htmlSnapshotS3Url && !playwrightTraceS3Url && !aspireUrl) return null;

  return (
    <Box>
      {screenshotS3Url && (
        <Link
          href={screenshotS3Url}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.screenshotLink}
          title="Open full-size screenshot"
        >
          <img
            src={screenshotS3Url}
            alt="Failure screenshot of the Aspire page"
            className={classes.screenshot}
          />
        </Link>
      )}
      <div className={classes.links}>
        {screenshotS3Url && (
          <Link href={screenshotS3Url} target="_blank" rel="noopener noreferrer">
            Open Screenshot
          </Link>
        )}
        {htmlSnapshotS3Url && (
          <Link href={htmlSnapshotS3Url} target="_blank" rel="noopener noreferrer">
            View HTML Snapshot
          </Link>
        )}
        {playwrightTraceS3Url && (
          <Link href={playwrightTraceS3Url} target="_blank" rel="noopener noreferrer">
            Download Playwright Trace
          </Link>
        )}
      </div>
      {aspireUrl && (
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">URL where error occurred:</Typography>
          <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
            {aspireUrl}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

DebugArtifactsSection.propTypes = {
  artifacts: PropTypes.shape({
    screenshotS3Url: PropTypes.string,
    htmlSnapshotS3Url: PropTypes.string,
    playwrightTraceS3Url: PropTypes.string,
    aspireUrl: PropTypes.string
  })
};

/** Collapsible raw payload snapshot of what was submitted. */
export const PayloadSnapshotSection = ({ payloadSnapshot }) => {
  const classes = useStyles();
  if (!payloadSnapshot || (typeof payloadSnapshot === 'object' && Object.keys(payloadSnapshot).length === 0)) {
    return null;
  }

  let rendered;
  try {
    rendered = JSON.stringify(payloadSnapshot, null, 2);
  } catch (err) {
    rendered = String(payloadSnapshot);
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">Payload Snapshot (raw submitted data)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <pre className={classes.snapshotPre}>{rendered}</pre>
      </AccordionDetails>
    </Accordion>
  );
};

PayloadSnapshotSection.propTypes = {
  payloadSnapshot: PropTypes.any
};
